/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { takeEvery, put, call, select } from "redux-saga/effects";
import endpoints, { endpointTypes } from "../../service";
import { fetchAPI } from "../../utils/service";
import {
    dateTimeFormatter,
    timeFormatter,
    getBOErrorNav,
    buildingAddressValidation,
    getPersistData,
    parseObject,
    isDirty,
    setPersistData,
} from "../../utils/utils";
import * as dynamicFormActionTypes from "./actionTypes";
import * as dynamicFormActionCreators from "./actionCreators";
import * as adminActionCreators from "../AdminDashboard/actionCreators";
import * as thankYouActionCreators from "../../components/ThankYou/actionCreators";
import * as GlobalAction from "../../globalStore/actions";
import * as CONFIG from "../../../config.json";
import { RootState } from "../../rootReducer";
import { PersistData } from "../../middleware/persistDataMiddleware";

const formLayoutRoute = endpoints[endpointTypes.FORMLAYOUT];
const reviewAndPayDataRoute = endpoints[endpointTypes.FETCH_REVIEW_AND_PAY_DETAILS];
const templateActionRoutes = endpoints[endpointTypes.HANDLE_TEMPLATE_ACTIONS];
const warrantyVarianceRoutes = endpoints[endpointTypes.WARRANTY_VARIANCE];

const getAdminInfo = (state: RootState) => {
    return state.adminData;
};

const getLayoutResponse = (state: RootState) => {
    return state.dynamicFormInfo && state.dynamicFormInfo.layoutResponse;
};

const getWarrantyDataFromLayoutResponse = (state: RootState) => {
    return state.dynamicFormInfo.layoutResponse && state.dynamicFormInfo.layoutResponse.WarrantyData;
};

const getWarrantyInfo = (state: RootState) => {
    return state.dynamicFormInfo.warrantyInfo;
};

const getWarrantyId = (state: RootState) => {
    return state.dynamicFormInfo && state.dynamicFormInfo.warrantyId;
};

export function* fetchLayoutData(action) {
    const state: RootState = yield select();
    const { isLayoutCallInProcess, layoutResponse } = state.dynamicFormInfo;
    const { JSONSchema, UISchema, ErrorSchema } = layoutResponse;
    const { isAlreadyPaymentInitiated } = state.payments;
    if (!isLayoutCallInProcess && !isAlreadyPaymentInitiated) {
        const idEndpoint = formLayoutRoute.endpoints.getFormLayoutDetails();
        let id = null;
        let endpoint = null;
        try {
            if (
                (!isLayoutCallInProcess && !layoutResponse) ||
                (layoutResponse && !JSONSchema && !UISchema && !ErrorSchema)
            ) {
                let response;
                yield put(dynamicFormActionCreators.layoutapiRunningStatus(true));
                try {
                    if (CONFIG.ACTIVATE_CDN) {
                        const responseData = yield call(fetchAPI, idEndpoint);
                        id = responseData.data.id;
                        endpoint = {
                            url: `${CONFIG.CDN_LAYOUT_URL + id}.json?version=${CONFIG.RWR_JSON_SCHEMA_VERSION}`,
                            method: "GET",
                            key: "formlayout",
                            disableHeader: true,
                        };
                    } else {
                        endpoint = formLayoutRoute.endpoints.getFormLayout(action.payload);
                    }
                    response = yield call(fetchAPI, endpoint);
                } catch (err) {
                    endpoint = formLayoutRoute.endpoints.getFormLayout(action.payload);
                    response = yield call(fetchAPI, endpoint);
                }
                if (response) {
                    const JSONSchema = parseObject(response.data.jsonSchema);
                    const UISchema = parseObject(response.data.uiSchema);
                    const ErrorSchema = parseObject(response.data.errorSchema) || [];
                    const result = {
                        forminstanceTypeId: response.data.id,
                        id: response.data.id,
                        JSONSchema,
                        UISchema,
                        ErrorSchema,
                    };

                    yield put(dynamicFormActionCreators.fetchFormLayoutDataSuccess({ value: result }));
                    window["AppInsightTrace"].trackTrace("fetchLayoutData API status code: " + response.status);
                }
            }
        } catch (err) {
            if (err && err.response && err.response.status === 403) {
                yield put(GlobalAction.showAccessDeniedError(err));
            } else {
                yield put(GlobalAction.showGlobalError(err));
            }
            yield put(dynamicFormActionCreators.layoutapiRunningStatus(false));
            // Application insight tracking for Exceptions in fetch call
            window["AppInsightTrace"].trackException(`fetchLayoutData ${err}`);
        }
    }
}

export function* fetchData() {
    yield takeEvery(dynamicFormActionTypes.FETCH_FORM_LAYOUT_DATA, fetchLayoutData);
}

export function* fetchSavedLayoutData(action) {
    const state: RootState = yield select();
    const { isAlreadyPaymentInitiated } = state.payments;
    const { activeAction } = state.myCartInfo;
    // const endpoint = formLayoutRoute.endpoints.getSavedFormLayout(action.payload.Id);
    // checking if form Layout already exists
    const LayoutResponse = yield select(getLayoutResponse);
    let { JSONSchema, UISchema, ErrorSchema, forminstanceTypeId } = LayoutResponse;
    let warrantyData;
    let endpoint = null;
    let formLayoutResponse = null;
    if (
        !LayoutResponse ||
        (LayoutResponse &&
            (Object.keys(LayoutResponse).length === 0 ||
                LayoutResponse.forminstanceTypeId !== action.payload.formInstanceId))
    ) {
        //getting the formlayout data
        try {
            if (CONFIG.ACTIVATE_CDN) {
                endpoint = {
                    url: `${CONFIG.CDN_LAYOUT_URL + action.payload.formInstanceId}.json?version=${
                        CONFIG.RWR_JSON_SCHEMA_VERSION
                    }`,
                    method: "GET",
                    key: "formlayout",
                    disableHeader: true,
                };
            } else {
                endpoint = formLayoutRoute.endpoints.getFormLayout(action.payload.formInstanceId);
            }
            formLayoutResponse = yield call(fetchAPI, endpoint);
        } catch (err) {
            endpoint = formLayoutRoute.endpoints.getFormLayout(action.payload.formInstanceId);
            formLayoutResponse = yield call(fetchAPI, endpoint);
        }
        try {
            JSONSchema = parseObject(formLayoutResponse.data.jsonSchema);
            UISchema = parseObject(formLayoutResponse.data.uiSchema);
            ErrorSchema = parseObject(formLayoutResponse.data.errorSchema) || [];
            forminstanceTypeId = formLayoutResponse.data.id;
        } catch (err) {
            // Application insight tracking for Exceptions in fetch call
            window["AppInsightTrace"].trackException(`fetchormLayout: ${err}`);
            yield put(GlobalAction.showGlobalError(err));
        }
    }

    // getting the warranty data
    const savedFormDataEndpoint = formLayoutRoute.endpoints.getSavedFormData(action.payload.Id);
    try {
        let waiverreceived = false;
        let variance = {};
        const response = yield call(fetchAPI, savedFormDataEndpoint);
        if (response) {
            warrantyData = parseObject(response.data.warrantyData);
            if (response.data.waiverReciever) {
                waiverreceived = true;
            }
            if (response.data.variance) {
                variance = response.data.variance
            }
            // const errors = parseObject(response.data.errors);
            if (isAlreadyPaymentInitiated) {
                warrantyData.formData.residentialWarrantyForm.NavigationMenu = "RP";
            } else if (
                !action.payload.isManualMenuSelection &&
                warrantyData.formData.residentialWarrantyForm.NavigationMenu != "BO"
            ) {
                warrantyData.formData.residentialWarrantyForm.NavigationMenu = "BO";
            }
            if (activeAction && activeAction === "review") {
                warrantyData.formData.residentialWarrantyForm.NavigationMenu = "RP";
            }
            const result = {
                id: action.payload.Id,
                JSONSchema,
                UISchema,
                ErrorSchema,
                forminstanceTypeId,
                WarrantyData: warrantyData.formData,
                isSavedDraft: true,
                waiverreceived,
                variance
            };

            if (
                Object.keys(JSONSchema).length === 0 ||
                Object.keys(UISchema).length === 0 ||
                ErrorSchema.length === 0 ||
                (result.WarrantyData && Object.keys(result.WarrantyData).length === 0)
            ) {
                yield put(
                    GlobalAction.showGlobalError({
                        errorMessage: "Something went wrong. Please try again.",
                        errorCode: "",
                    })
                );
                yield put(GlobalAction.updateActiveScreen("draftList"));
                action.payload.failureCallback &&
                    action.payload.failureCallback({
                        errorMessage: "Something went wrong. Please try again.",
                        errorCode: "",
                    });
                window["AppInsightTrace"].trackException(`fetchSavedFormData failed`);
            } else {
                yield put(adminActionCreators.updateWaiverReceived(waiverreceived));
                if (!isAlreadyPaymentInitiated && !action.payload.isManualMenuSelection) {
                    yield put(
                        dynamicFormActionCreators.getErrorResponseOnNavChange({ id: action.payload.Id, result: result })
                    );
                } else {
                    yield put(dynamicFormActionCreators.fetchSavedFormLayoutDataSuccess({ value: result }));
                    yield put(dynamicFormActionCreators.getErrorResponseOnNavChange({ id: action.payload.Id }));
                }
                // const response = {
                //     errors: response.data.errors,
                // };
                // this.props.dynamicFormActions.fetchFormErrorResponseSuccess(response);
                action.payload.successCallBack && action.payload.successCallBack();
                window["AppInsightTrace"].trackTrace("fetchSavedFormData API status code: " + response.status);
            }
        }
    } catch (err) {
        yield put(GlobalAction.showGlobalError(err));
        action.payload.failureCallback && action.payload.failureCallback(err);
        window["AppInsightTrace"].trackException(`fetchSavedFormData ${err}`);
    }
}

export function* fetchSavedFormLayoutData() {
    yield takeEvery(dynamicFormActionTypes.FETCH_SAVED_FORM_LAYOUT_DATA, fetchSavedLayoutData);
}

export function* fetchSavedData(action) {
    const endpoint = formLayoutRoute.endpoints.getSavedFormData(action.payload.Id);

    try {
        const response = yield call(fetchAPI, endpoint);
        const warrantyData = parseObject(response.data.warrantyData);
        if (action.payload.updateNavigation) {
            warrantyData.formData.residentialWarrantyForm.NavigationMenu = action.payload.updateNavigation;
        }
        const result = { id: response.data.id, WarrantyData: warrantyData.formData };
        if (response) {
            yield put(dynamicFormActionCreators.fetchSavedFormDataSuccess({ value: result }));
            action.payload.successCallBack && action.payload.successCallBack();
            window["AppInsightTrace"].trackTrace("fetchSavedData API status code: " + response.status);
        }
    } catch (err) {
        yield put(GlobalAction.showGlobalError(err));
        // Application insight tracking for Exceptions in fetch call
        window["AppInsightTrace"].trackException(`fetchSavedData ${err}`);
        action.payload.failureCallback && action.payload.failureCallback(err);
    }
}

export function* fetchSavedFormData() {
    yield takeEvery(dynamicFormActionTypes.FETCH_SAVED_FORM_DATA, fetchSavedData);
}

export function* fetchSavedDataAndCopy(action) {
    const { warrantyId } = getPersistData() || {};
    const endpoint = formLayoutRoute.endpoints.getSavedFormData(warrantyId);

    try {
        const response = yield call(fetchAPI, endpoint);
        const warrantyData = parseObject(response.data.warrantyData);
        warrantyData.formData.residentialWarrantyForm.NavigationMenu = "BO";
        const result = { id: null, WarrantyData: warrantyData.formData };
        const persistData: PersistData = getPersistData() || {};
        delete persistData.warrantyId;
        setPersistData(persistData);
        if (response) {
            yield put(GlobalAction.updateActiveScreen("NewForm"));
            yield put(dynamicFormActionCreators.fetchSavedFormDataSuccess({ value: result }));
            window["AppInsightTrace"].trackTrace("fetchSavedData API status code: " + response.status);
        }
    } catch (err) {
        yield put(GlobalAction.showGlobalError(err));
        // Application insight tracking for Exceptions in fetch call
        window["AppInsightTrace"].trackException(`fetchSavedData ${err}`);
    }
}

export function* fetchSavedFormDataAndCopy() {
    yield takeEvery(dynamicFormActionTypes.FETCH_SAVED_FORM_DATA_AND_COPY, fetchSavedDataAndCopy);
}

export const updateAfterSaveMsg = (timeStamp) => {
    return {
        desktop: `Last saved ${dateTimeFormatter(timeStamp)}`,
        mobile: `Last saved ${timeFormatter(timeStamp)}`,
    };
};
const validateReRoofType = (projectDetails) => {
    if (projectDetails && projectDetails.warrantyType !== "SYSTEM_PLUS") {
        if (
            projectDetails.jobType &&
            projectDetails.jobType.typeOfJob === "RR" &&
            projectDetails.jobType.reRoofType === "RCOES"
        ) {
            projectDetails.jobType.reRoofType = "";
        } 
    }
};
export function mergeWarrantyInfoSave(existingWarantyData1, currentWarrantyData1) {
    if (Object.keys(currentWarrantyData1).length === 0) {
        currentWarrantyData1 = { ...existingWarantyData1 };
    }
    const currentWarrantyData = JSON.parse(JSON.stringify(currentWarrantyData1));
    if (!existingWarantyData1) {
        return currentWarrantyData;
    }
    const existingWarantyData = JSON.parse(JSON.stringify(existingWarantyData1));
    if (
        existingWarantyData.residentialWarrantyForm.buildingAndOwnerInfo &&
        existingWarantyData.residentialWarrantyForm.projectDetails
    ) {
        if (currentWarrantyData.residentialWarrantyForm.projectDetails) {
            currentWarrantyData.residentialWarrantyForm["buildingAndOwnerInfo"] =
                existingWarantyData.residentialWarrantyForm.buildingAndOwnerInfo;
        } else if (currentWarrantyData.residentialWarrantyForm.buildingAndOwnerInfo) {
            currentWarrantyData.residentialWarrantyForm["projectDetails"] =
                existingWarantyData.residentialWarrantyForm.projectDetails;
        }
    } else if (
        existingWarantyData.residentialWarrantyForm.buildingAndOwnerInfo &&
        currentWarrantyData.residentialWarrantyForm.projectDetails
    ) {
        currentWarrantyData.residentialWarrantyForm["buildingAndOwnerInfo"] =
            existingWarantyData.residentialWarrantyForm.buildingAndOwnerInfo;
    } else if (
        existingWarantyData.residentialWarrantyForm.projectDetails &&
        currentWarrantyData.residentialWarrantyForm.buildingAndOwnerInfo
    ) {
        currentWarrantyData.residentialWarrantyForm["projectDetails"] =
            existingWarantyData.residentialWarrantyForm.projectDetails;
    }
    // Remove the project details object from formdata if the warranty Type is not selected.
    if (
        currentWarrantyData.residentialWarrantyForm.projectDetails &&
        typeof currentWarrantyData.residentialWarrantyForm.projectDetails.warrantyType !== "string"
    ) {
        delete currentWarrantyData.residentialWarrantyForm.projectDetails;
    }
    validateReRoofType(currentWarrantyData.residentialWarrantyForm.projectDetails);
    return currentWarrantyData;
}
export function* mergeFormDatatoStore(action) {
    const state: RootState = yield select();
    const { formId, warrantyId } = state.dynamicFormInfo;
    const existingWarantyData = yield select(getWarrantyDataFromLayoutResponse);
    const currentWarrantyData = yield select(getWarrantyInfo);
    const warrantyData = mergeWarrantyInfoSave(existingWarantyData, currentWarrantyData);
    const persistData: PersistData = getPersistData() || {};
    try {
        if (
            persistData.forceSaveWarranty ||
            !existingWarantyData ||
            !currentWarrantyData ||
            isDirty(existingWarantyData.residentialWarrantyForm, currentWarrantyData.residentialWarrantyForm)
        ) {
            delete persistData.forceSaveWarranty;
            setPersistData(persistData);
            if (buildingAddressValidation(warrantyData, CONFIG["mandatoryFieldUtilities"])) {
                if (warrantyId) {
                    const payload = { Id: warrantyId, formData: { ...warrantyData } };
                    yield call(() => updateDraftWarranty({ payload }));
                } else {
                    const payload = { Id: formId, formData: { ...warrantyData } };
                    yield call(() => saveDraftWarranty({ payload }));
                }
            }
        }
        const result = { id: action.id || action.payload.id, WarrantyData: { ...warrantyData } };
        if (result) {
            yield put(dynamicFormActionCreators.fetchSavedFormDataSuccess({ value: result }));
            action.payload.successCallBack();
        }
    } catch (err) {}
}
export function* fetchMergeFormDatatoStore() {
    yield takeEvery(dynamicFormActionTypes.MERGE_FORM_DATA_TO_STORE, mergeFormDatatoStore);
}

export function* saveDraftWarranty(action) {
    const existingWarantyData = yield select(getWarrantyDataFromLayoutResponse);
    const currentWarrantyData = yield select(getWarrantyInfo);
    const warrantyData = mergeWarrantyInfoSave(existingWarantyData, currentWarrantyData);
    const adminInfo = yield select(getAdminInfo);
    let endpoint = {};
    if (adminInfo && adminInfo.userInfo && adminInfo.userInfo.isAdmin) {
        endpoint = formLayoutRoute.endpoints.saveDraftForm({
            formData: warrantyData,
            Id: action.payload.Id,
            dateReceived: adminInfo.warrantyReceivedDate,
            waiverReceived: adminInfo.waiverReceived,
        });
    } else {
        endpoint = formLayoutRoute.endpoints.saveDraftForm({
            formData: warrantyData,
            Id: action.payload.Id,
        });
    }
    // const endpoint = formLayoutRoute.endpoints.saveDraftForm({
    //     formData: warrantyData,
    //     Id: action.payload.Id,
    // });
    try {
        const response = yield call(fetchAPI, endpoint);
        window["AppInsightTrace"].trackTrace("saveDraftWarranty API status code: " + response);
        if (response && response.data) {
            const transformMessage = updateAfterSaveMsg(response.data.saveTime);
            yield put(dynamicFormActionCreators.updateWarrentyId({ payload: response.data.id }));
            yield put(
                dynamicFormActionCreators.saveDraftFormSuccess({
                    payload: {
                        mobileMsg: transformMessage.mobile,
                        desktopMsg: transformMessage.desktop,
                        updateWarrantyData: warrantyData,
                    },
                })
            );
            action.payload.successCallBack && action.payload.successCallBack(response.data);
        } else {
            action.payload.failureCallback && action.payload.failureCallback(response);
        }
    } catch (err) {
        // Application insight tracking for Exceptions in fetch call
        window["AppInsightTrace"].trackException(`saveDraftWarranty ${err}`);
        if (err && err.response && err.response.data && err.response.data.StatusCode === 400) {
            yield put(dynamicFormActionCreators.saveDraftFormSuccess({ payload: err.response.data.Message }));
            action.payload.failureCallback && action.payload.failureCallback(err);
        } else {
            yield put(GlobalAction.showGlobalError(err));
            action.payload.failureCallback && action.payload.failureCallback(err);
        }
    }
}

export function* saveDraftItem() {
    yield takeEvery(dynamicFormActionTypes.SAVE_DRAFT_FORM, saveDraftWarranty);
}

export function* updateDraftWarranty(action) {
    try {
        const existingWarantyData = yield select(getWarrantyDataFromLayoutResponse);
        const currentWarrantyData = yield select(getWarrantyInfo);
        const updateWarrantyData = mergeWarrantyInfoSave(existingWarantyData, currentWarrantyData);
        const adminInfo = yield select(getAdminInfo);
        let endpoint = {};
        if (adminInfo && adminInfo.userInfo && adminInfo.userInfo.isAdmin) {
            endpoint = formLayoutRoute.endpoints.updateDraftForm({
                formData: updateWarrantyData,
                Id: action.payload.Id,
                dateReceived: adminInfo.warrantyReceivedDate,
                waiverReceived: adminInfo.waiverReceived,
            });
        } else {
            endpoint = formLayoutRoute.endpoints.updateDraftForm({
                formData: updateWarrantyData,
                Id: action.payload.Id,
            });
        }
        // const endpoint = formLayoutRoute.endpoints.updateDraftForm({
        //     formData: updateWarrantyData,
        //     Id: action.payload.Id,
        // });
        const response = yield call(fetchAPI, endpoint);
        window["AppInsightTrace"].trackTrace("updateDraftWarranty:" + response);
        if (response && response.data) {
            const transformMessage = updateAfterSaveMsg(response.data.saveTime);
            yield put(
                dynamicFormActionCreators.updateDraftFormSuccess({
                    payload: {
                        mobileMsg: transformMessage.mobile,
                        desktopMsg: transformMessage.desktop,
                        updateWarrantyData,
                    },
                })
            );
            action.payload.successCallBack && action.payload.successCallBack(response.data);
        } else {
            action.payload.failureCallback && action.payload.failureCallback(response);
        }
    } catch (err) {
        // Application insight tracking for Exceptions in fetch call
        window["AppInsightTrace"].trackException("updateDraftWarranty:" + err);
        if (err && err.response && err.response.data && err.response.data.StatusCode === 400) {
            yield put(dynamicFormActionCreators.saveDraftFormSuccess({ payload: err.response.data.Message }));
            yield put(dynamicFormActionCreators.updateDraftFormFailure({ payload: err.response.data.Message }));
            action.payload.failureCallback && action.payload.failureCallback(err);
        } else {
            yield put(GlobalAction.showGlobalError(err));
            action.payload.failureCallback && action.payload.failureCallback(err);
        }
    }
}

export function* updateDraftForm() {
    yield takeEvery(dynamicFormActionTypes.UPDATE_DRAFT_FORM, updateDraftWarranty);
}
export function* fetchUpdatedBuildAddressCountryCode(action) {
    yield put(dynamicFormActionCreators.getBuildAddressCountryCodeAsync({ value: action.payload }));
}
export function* fetchUpdatedMailAddressCountryCode(action) {
    yield put(dynamicFormActionCreators.getMailAddressCountryCodeAsync({ value: action.payload }));
}
export function* fetchBuildingAddressCountryCode() {
    yield takeEvery(dynamicFormActionTypes.GET_BUILDING_ADDRESS_COUTRY_CODE, fetchUpdatedBuildAddressCountryCode);
}
export function* fetchMailAddressCountryCode() {
    yield takeEvery(dynamicFormActionTypes.GET_MAIL_ADDRESS_COUTRY_CODE, fetchUpdatedMailAddressCountryCode);
}
/**`
 * @function autoSaveFormData
 * @description
 * AutoSave the formData which stored in redux store
 * Only execute save if warrantyId available
 * @ignore
 */
export function* autoSaveFormData() {
    const state: RootState = yield select();
    const { formId, warrantyId, warrantyInfo } = state.dynamicFormInfo;
    const existingWarantyData = yield select(getWarrantyDataFromLayoutResponse);
    const currentWarrantyData = yield select(getWarrantyInfo);
    const warrantyData = mergeWarrantyInfoSave(existingWarantyData, currentWarrantyData);
    const persistData: PersistData = getPersistData() || {};
    if (
        persistData.forceSaveWarranty ||
        !existingWarantyData ||
        !currentWarrantyData ||
        isDirty(existingWarantyData.residentialWarrantyForm, currentWarrantyData.residentialWarrantyForm)
    ) {
        delete persistData.forceSaveWarranty;
        setPersistData(persistData);
        if (buildingAddressValidation(warrantyData, CONFIG["mandatoryFieldUtilities"])) {
            if (warrantyId) {
                const payload = { Id: warrantyId, formData: { ...warrantyInfo } };
                yield call(() => updateDraftWarranty({ payload }));
            } else {
                const payload = { Id: formId, formData: { ...warrantyInfo } };
                yield call(() => saveDraftWarranty({ payload }));
            }
        }
    }
}
export function* autoSaveForm() {
    yield takeEvery(dynamicFormActionTypes.AUTO_SAVE_FORM, autoSaveFormData);
}

/**
 * @function fetchReviewAndPayData
 * @param action
 * @description This basically call the API to get the data
 * and calls a action mapper to set the response to reducer
 * If error it throws the same.
 * @ignore
 */
export function* fetchReviewAndPayData(action) {
    // building the URL for making an API call
    const existingWarantyData = yield select(getWarrantyDataFromLayoutResponse);
    const currentWarrantyData = yield select(getWarrantyInfo);
    const warrantyData = mergeWarrantyInfoSave(existingWarantyData, currentWarrantyData);
    const endpoint = reviewAndPayDataRoute.endpoints.getReviewAndPay({
        formData: warrantyData,
        Id: action.payload || 0,
    });
    try {
        // Calling the API to get the response. -- TODO  api once ready call
        const response = yield call(fetchAPI, endpoint);
        if (response && response.data) {
            // call the set response data to set the response to redux store
            yield put(dynamicFormActionCreators.setReviewAndPayData({ value: response.data }));
            window["AppInsightTrace"].trackTrace("fetchReviewAndPayData: " + response);
        }
    } catch (err) {
        // Application insight tracking for Exceptions in fetch call
        window["AppInsightTrace"].trackException("fetchReviewAndPayData: " + err);
        yield put(GlobalAction.showGlobalError(err));
    }
}

/**
 *
 * @param action
 * @description
 * This function basically gets and handles to and fro linking of the
 * response to action mapper by making api call in fetchReviewAndPayData
 * This function should be listed in root saga.
 * @ignore
 */
export function* getReviewAndPayData() {
    yield takeEvery(dynamicFormActionTypes.FETCH_REVIEW_AND_PAY_FORM_DATA, fetchReviewAndPayData);
}

export function* getFormErrorResponse(action) {
    const existingWarantyData = yield select(getWarrantyDataFromLayoutResponse);
    const currentWarrantyData = yield select(getWarrantyInfo);
    const warrantyData = mergeWarrantyInfoSave(existingWarantyData, currentWarrantyData);

    const fetchErrorResponseEndpoint = formLayoutRoute.endpoints.getErrorResponse({
        id: action.id,
        formData: (action.result && action.result.WarrantyData) || warrantyData,
    });
    try {
        const response = yield call(fetchAPI, fetchErrorResponseEndpoint);
        // const response = yield call(fetchAPI, endpoint);
        if (response && response.data) {
            // const transformMessage = updateAfterSaveMsg(response.data.saveTime);
            yield put(dynamicFormActionCreators.fetchFormErrorResponseSuccess(response.data));
            if (
                action.result &&
                response.data &&
                response.data.errors &&
                response.data.errors.formValidationSummary &&
                Object.keys(response.data.errors.formValidationSummary).length !== 0
            ) {
                const navOption = getBOErrorNav(response.data.errors.formValidationSummary);
                action.result.WarrantyData.residentialWarrantyForm.NavigationMenu = navOption;
            }
            action.successCallBack && action.successCallBack(response.data.errors.formValidationSummary);
            // action.payload.successCallBack && action.payload.successCallBack(response.data);
        }
        if (action.result) {
            yield put(dynamicFormActionCreators.fetchSavedFormLayoutDataSuccess({ value: action.result }));
        }
        // action.successCallBack && action.successCallBack();
    } catch (err) {
        if (action.result) {
            yield put(dynamicFormActionCreators.fetchSavedFormLayoutDataSuccess({ value: action.result }));
        }
        // action.successCallBack && action.successCallBack();
        yield put(GlobalAction.showGlobalError(err));
        // action.payload.failureCallback && action.payload.failureCallback(err);
    }
}

//pass appropriate
export function* getErrorResponseOnNavChange() {
    yield takeEvery(dynamicFormActionTypes.FETCH_ERROR_RESPONSE, getFormErrorResponse);
}
/**
 *
 * @param formatTemplateResponse
 * @param warrantyType
 * @description Manage template to fit the form of array of warrenty types
 * @ignore
 */
export function formatTemplateResponse(savedTemplates, warrantyType) {
    const [name, id, type, CREATED, WarrentyTypeCode] = ["name", "id", "type", "created", "WarrentyTypeCode"];
    const listLength = savedTemplates.length;

    const templateList = {};
    for (let j = 0; j <= listLength - 1; j++) {
        const warrantyTypeOfList = savedTemplates[j].warrentyTypeCode;
        if (templateList[warrantyTypeOfList]) {
            const storeObj: any = {};
            storeObj[name] = savedTemplates[j].templateName;
            storeObj[id] = savedTemplates[j].templateId;
            storeObj[WarrentyTypeCode] = savedTemplates[j].WarrentyTypeCode;
            storeObj[type] = CREATED;
            templateList[warrantyTypeOfList].push(storeObj);
        } else {
            const storeObj: any = {};
            templateList[warrantyTypeOfList] = [];
            storeObj[name] = savedTemplates[j].templateName;
            storeObj[id] = savedTemplates[j].templateId;
            storeObj[WarrentyTypeCode] = savedTemplates[j].WarrentyTypeCode;
            storeObj[type] = CREATED;
            templateList[warrantyTypeOfList].push(storeObj);
        }
    }

    return templateList;
}
/**
 * @function  manageSaveTemplate
 * @param action
 * @description
         1. get the existings store value.
         2. check existing store has value or not.
         3. if it has value with create then send a respone already available
         4. if not available then make an API call to save the data
         5. then update the store back
 @ignore
 */

export function* manageSaveTemplate(action) {
    const [saveTemplateURI, getAllTemplatesURI, warrantyType, ERROR_CODE, fieldId] = [
        templateActionRoutes.endpoints.saveTemplate(action.payload),
        templateActionRoutes.endpoints.getAllTemplates(action.payload),
        action.payload.WarrentyTypeCode,
        "400",
        action.payload.fieldId,
    ];
    try {
        let mergedResponse: any;

        // If not already saved call the save Template API
        const saveResponse = yield call(fetchAPI, saveTemplateURI);
        // if the template is not already set get the response
        const getResponse = yield call(fetchAPI, getAllTemplatesURI);
        // When save is successful then we need to update the response and store
        if (saveResponse && saveResponse.data) {
            let successMessage = "";
            mergedResponse = formatTemplateResponse(getResponse.data, warrantyType);
            successMessage += `"${action.payload.templateName}" has been saved for future use`;
            // Updating the store with the name templates
            yield put(dynamicFormActionCreators.setTemplate({ value: mergedResponse }));
            yield put(dynamicFormActionCreators.setTemplateList({ value: getResponse.data }));
            yield put(GlobalAction.showGlobalSuccssMsg(successMessage));
            action.payload.successCallBack && action.payload.successCallBack();
        }
    } catch (err) {
        action.payload.failureCallback && action.payload.failureCallback(err);
        if (err && err.response && err.response.data && err.response.data.StatusCode == ERROR_CODE) {
            yield put(
                dynamicFormActionCreators.addFieldIdToStore({
                    id: fieldId.$id,
                    msg: err.response.data.Message,
                })
            );
        } else {
            yield put(GlobalAction.showGlobalError(err));
        }
        const getResponse = yield call(fetchAPI, getAllTemplatesURI);
        const mergedResponse = formatTemplateResponse(getResponse.data, warrantyType);
        yield put(dynamicFormActionCreators.setTemplate({ value: mergedResponse }));
        yield put(dynamicFormActionCreators.setTemplateList({ value: getResponse.data }));
    }
}

/**
 * @function  saveTemplate
 * @param action
 * @description This function basically gets call from action creator
                1. With the save pay load
                2.Which in turns saveOrUpdateTemplate
 @ignore
 */
export function* saveTemplate() {
    yield takeEvery(dynamicFormActionTypes.SAVE_TEMPLATE, manageSaveTemplate);
}

/**
 * @function  manageGetTemplates
 * @param action
 * @description
        1. It fetches all te template from the data base then set it to save
        2. Fetch ztore
 @ignore
 */
export function* manageGetTemplates(action) {
    const [getAllTemplateURI, manageTemplate, updateSchma] = [
        templateActionRoutes.endpoints.getAllTemplates(action.payload),
        "manageTemplate",
        "updateSchma",
    ];
    try {
        const respnse = yield call(fetchAPI, getAllTemplateURI);
        if (respnse && respnse.data) {
            const mergedResponse = formatTemplateResponse(respnse.data, action.payload.warrentyTypeCode);
            yield put(dynamicFormActionCreators.setTemplateList({ value: respnse.data }));
            yield put(dynamicFormActionCreators.setTemplate({ value: mergedResponse }));
        }
    } catch (err) {
        yield put(GlobalAction.showGlobalError(err));
    }
}
/**
 * @function  getTemplates
 * @param action
 * @description
            1. It will be used to get all the saved templates
            2. Which can be used in load template call to render the
            3. saved template data like project details etc.
 @ignore
 */
export function* getTemplates() {
    yield takeEvery(dynamicFormActionTypes.FETCH_SAVED_TEMPLATES, manageGetTemplates);
}

/**
 * @function  loadTemplatesById
 * @param action
 * @description
            1. loadTemplatesById
 @ignore
 */
export function* loadTemplatesById(action) {
    const getByTemplateIdURI = templateActionRoutes.endpoints.getTemplateById(action.payload.templateId);
    try {
        const respnse = yield call(fetchAPI, getByTemplateIdURI);
        if (respnse && respnse.data) {
            yield put(dynamicFormActionCreators.setProjectDetails({ value: JSON.parse(respnse.data.templateData) }));
            const persistData: PersistData = getPersistData() || {};
            persistData.forceSaveWarranty = true;
            setPersistData(persistData);
            let successMessage = "";
            successMessage += action.templateName + " has been successfully loaded into this form";
            action.payload.successCallBack && action.payload.successCallBack(respnse.data);
            yield put(GlobalAction.showGlobalSuccssMsg(successMessage));
        }
    } catch (err) {
        action.payload.failureCallback && action.payload.failureCallback(err);
        yield put(GlobalAction.showGlobalError(err));
    }
}
/**
 * @function  getTemplatesById
 * @param action
 * @description
            1. GetTemplateBYId
 @ignore
 */
export function* getTemplatesById() {
    yield takeEvery(dynamicFormActionTypes.GET_TEMPLATE_BY_ID, loadTemplatesById);
}
/**
 * @function  manageDeleteTemplate
 * @param action
 * @description
         1. call delete api to delete.
         2. call get by ids
         3. filter the response
         4. now alter the response such that it matches the savedTemplate store
         3. How to update the existing store as deleted
 @ignore
 */
export function* manageDeleteTemplate(action) {
    const deleteTemplateURI = templateActionRoutes.endpoints.deleteTemplates(action.payload.templateIds);
    try {
        const delResponse = yield call(fetchAPI, deleteTemplateURI);
        if (delResponse && delResponse.data) {
            const getResponse = yield call(fetchAPI, templateActionRoutes.endpoints.getAllTemplates(action.payload));
            const formatedResp = formatTemplateResponse(getResponse.data, action.payload.WarrentyTypeCode);
            action.payload.successCallBack && action.payload.successCallBack(delResponse.data);
            yield put(dynamicFormActionCreators.setTemplate({ value: formatedResp }));
            yield put(dynamicFormActionCreators.setTemplateList({ value: getResponse.data }));
        }
    } catch (err) {
        action.payload.failureCallback && action.payload.failureCallback(err);
        yield put(GlobalAction.showGlobalError(err));
    }
}
/**
 * @function  deleteTemplates
 * @param action
 * @description
         1. get the existings store value.
         2. check existing store has value or not.
         3. if it has value with create then send a respone already available
         4. if not available then make an API call to save the data
         5. then update the store back
 @ignore
 */
export function* deleteTemplates() {
    yield takeEvery(dynamicFormActionTypes.DELETE_AND_UPDATE_TEMPLATE, manageDeleteTemplate);
}

export function* checkfieldEditable(action) {
    const editableCheckEndpoint = formLayoutRoute.endpoints.checkEditable(action.params);
    try {
        const response = yield call(fetchAPI, editableCheckEndpoint);
        if (response && response.data) {
            yield put(dynamicFormActionCreators.checkfieldEditableSuccess(response.data));
        }
    } catch (err) {
        yield put(GlobalAction.showGlobalError(err));
    }
}

export function* checkEditable() {
    yield takeEvery(dynamicFormActionTypes.FETCH_EDITABLE_STATUS, checkfieldEditable);
}

export function* validateBuildingAddress(action) {
    const existingWarantyData = yield select(getWarrantyDataFromLayoutResponse);
    const currentWarrantyData = yield select(getWarrantyInfo);
    const warrantyData = mergeWarrantyInfoSave(existingWarantyData, currentWarrantyData);
    if (
        warrantyData &&
        warrantyData.residentialWarrantyForm &&
        warrantyData.residentialWarrantyForm.NavigationMenu === "BO"
    ) {
        action.successCallback &&
            action.successCallback(buildingAddressValidation(warrantyData, CONFIG["mandatoryFieldUtilities"]));
    } else {
        action.successCallback && action.successCallback(true);
    }
}
export function* validateBuildingAddr() {
    yield takeEvery(dynamicFormActionTypes.VALIDATE_BUILDING_ADDRESS, validateBuildingAddress);
}
export function* updateSubmittedWarranty(action) {
    const warrantyId = yield select(getWarrantyId);
    const existingWarantyData = yield select(getWarrantyDataFromLayoutResponse);
    const currentWarrantyData = yield select(getWarrantyInfo);
    const warrantyData = mergeWarrantyInfoSave(existingWarantyData, currentWarrantyData);
    const object = {
        Id: warrantyId,
        formData: warrantyData,
        WaiverReceived: action.payload.WaiverReceived,
    };
    const endpoint = formLayoutRoute.endpoints.updateSubmittedWarranty(object);
    try {
        const response = yield call(fetchAPI, endpoint);
        window["AppInsightTrace"].trackTrace("on Re-submitting a Warranty: " + response.status);
        if (response && response.data) {
            if (response.data.warrantyId) {
                const warrantynumber =  response.data.warrantyNumber;
                const paymentDirectorTransactionId = response.data.paymentDirectorTransactionId;
                 // Storing warranty number & paymentDirectorTransactionId to use on Thank You page for Resubmit Warranties
                sessionStorage.setItem('warrantynumber', warrantynumber);
                sessionStorage.setItem('paymentDirectorTransactionId', paymentDirectorTransactionId);

                yield put(thankYouActionCreators.updateNavigation(response.data));
            }
            action.payload.successCallBack && action.payload.successCallBack(response.data);
        }
    } catch (err) {
        // Application insight tracking for Exceptions in fetch call
        window["AppInsightTrace"].trackException(`error on on Re-submitting a Warranty: ${err}`);
        yield put(GlobalAction.showGlobalError(err));
        action.payload.failureCallback && action.payload.failureCallback(err);
    }
}
export function* modifySubmittedWarranty() {
    yield takeEvery(dynamicFormActionTypes.SUBMIT_WARRANTY, updateSubmittedWarranty);
}

export function* GetContractorFormSettings() {
    const endpoint = formLayoutRoute.endpoints.GetContractorFormSettings();
    try {
        const response = yield call(fetchAPI, endpoint);
        yield put(dynamicFormActionCreators.SetContractorFormSettings(response.data));
    } catch (err) {
        yield put(GlobalAction.showGlobalError(err));
    }
}

export function* GetContractorFormSetting() {
    yield takeEvery(dynamicFormActionTypes.GET_CONTRACTOR_FORM_SETTINGS, GetContractorFormSettings);
}

export function* saveWarrantyVariance(action) {
    const payload = action.payload;
    const warrantyId =  action.warrantyId;
    
    const endpoint = warrantyVarianceRoutes.endpoints.saveWarrantyVariance(warrantyId, payload);
    try {
        const response = yield call(fetchAPI, endpoint);
        if(response) {
            yield put(dynamicFormActionCreators.saveWarrantyVarianceSuccess(response))
        }
    } catch (err) {
            yield put(dynamicFormActionCreators.saveWarrantyVarianceFailure(err));
    }
}

export function* warrantyVarianceWatcher() {
    yield takeEvery(dynamicFormActionTypes.SAVE_WARRANTY_VARIANCE, saveWarrantyVariance);
}